import { Template } from '@bynder-studio/render-core';
import { ImportTargetDpi } from 'packages/store/importTemplate/types';
import { gatewayClient, createService } from './connectors/httpClient';
import { gatewayStream } from './connectors/sseClient';

export enum RequestTypes {
    ANALYSIS = 'ANALYSIS',
    IMPORT = 'IMPORT',
}

type CreateTemplateAnalysisBaseData = {
    type: RequestTypes.ANALYSIS;
    customerId: number;
    creatorId: string;
};

type CreateTemplateImportBaseData = {
    type: RequestTypes.IMPORT;
    customerId: number;
    collectionId: string;
    creatorId: string;
    multiDesignMode: boolean;
    indesignImportPropertiesDto?: {
        targetDpi: ImportTargetDpi;
    };
};

type CreateTemplateAnalysisGlobalData = CreateTemplateAnalysisBaseData & {
    mediaId: string;
};

type CreateTemplateImportGlobalData = CreateTemplateImportBaseData & {
    mediaId: string;
};

type CreateTemplateAnalysisLocalData = CreateTemplateAnalysisBaseData & {
    storagePath: string;
};

type CreateTemplateImportLocalData = CreateTemplateImportBaseData & {
    storagePath: string;
};

type CreateTemplateFigmaData = CreateTemplateImportBaseData & {
    figmaImportPropertiesDto: {
        fileKey: string;
        frameIds: string[];
    };
};

export type CreateTemplateData =
    | CreateTemplateAnalysisGlobalData
    | CreateTemplateAnalysisLocalData
    | CreateTemplateImportGlobalData
    | CreateTemplateImportLocalData
    | CreateTemplateFigmaData;

const httpClient = gatewayClient('/design-creation-requests');
const progressStream = gatewayStream('/design-creation-requests');

const templatesServiceFromDesignFile = createService(httpClient, (client) => ({
    createTemplate(data: CreateTemplateData) {
        return client.post<Template>('', data);
    },
}));

export type DesignCreationItem = {
    status: string;
    progress: number;
    creativeId: number | null;
};

// The backend uses @JsonInclude(NON_NULL), so the unused one is absent rather than null.
export type DesignCreationProgressItem = {
    id: number;
    status: string;
    progress: number;
    errorMessage?: string;
    designCreationData?: {
        designCreationResponses: { name: string; width: number; height: number; elements: unknown[] }[];
    };
    designCreationRequestItems?: DesignCreationItem[];
};

// Forwards the stream's own unsubscribe: consumers are thunks, so there is no hook to supply one.
export function listenToDesignCreationProgress(
    designCreationRequestIds: number[],
    onItems: (items: DesignCreationProgressItem[], unsubscribe: () => void) => void,
    onClose?: () => void,
): () => void {
    return progressStream.subscribe<{ totalItems: number; items: DesignCreationProgressItem[] }, { ids: number[] }>(
        '/list/stream',
        {
            eventName: 'progress',
            // `ids`, per CreativeListRequestDto; `designCreationRequestIds` is the STOMP body key.
            body: { ids: designCreationRequestIds },
            onMessage: ({ items }, unsubscribe) => onItems(items, unsubscribe),
            onClose,
        },
    );
}

export default templatesServiceFromDesignFile;
